import React, { useState } from "react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import CinematicShell from "@/components/cinematic/CinematicShell";
import { PartnersForgeStill } from "@/components/cinematic/stills";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

export default function PartnersPage() {
  const [form, setForm] = useState({
    org_name: "", contact_name: "", email: "", phone: "",
    website: "", services_offered: "", est_monthly_volume: "", notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/intake/partner-application", form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PublicNav />
      <div className="relative dt6-cinematic-vignette" style={{ minHeight: "440px" }}>
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/PartnersForge")}
          stillSrc={PartnersForgeStill}
          testId="partners-hero-scene"
          className="dt6-cinematic-grain"
          style={{ position: "absolute", inset: 0, zIndex: 0 }}
        />
        <Section className="pt-16 pb-8 relative dt6-cinematic-copy" testId="partners-hero">
          <div className="dt6-chip dt6-chip-primary mb-4">/ WHITE-LABEL PARTNER</div>
          <h1 className="font-heading text-5xl md:text-6xl font-black leading-tight">
            Deliver DT6 <span style={{ color: "var(--dt6-primary)" }}>under your brand.</span>
          </h1>
          <p className="mt-4 text-lg text-[color:var(--dt6-muted)] max-w-3xl">
            Consultants and agencies: put the six-module stack in front of your clients under your name.
            Wholesale pricing sheet, submission portal, custom brand color and logo.
          </p>
        </Section>
      </div>

      <Section className="py-8" testId="partners-mechanics">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { t: "01 / Apply", d: "Fill out the application. We qualify within 3 business days." },
            { t: "02 / Configure", d: "Set your display name, accent color, and logo in the partner portal." },
            { t: "03 / Deliver", d: "Submit client orders. Receive deliverables. Bill your client. Keep the margin." },
          ].map((s) => (
            <div key={s.t} className="dt6-card p-6" data-testid={`partner-step-${s.t.slice(0,2)}`}>
              <div className="font-mono text-[0.75rem] tracking-[0.14em] text-[color:var(--dt6-primary)]">{s.t}</div>
              <p className="mt-3 text-sm text-[color:var(--dt6-muted)]">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-12" testId="partners-form-section">
        <div className="dt6-card p-8 md:p-10">
          <div className="dt6-chip mb-4">/ APPLICATION</div>
          <h2 className="font-heading text-3xl font-bold mb-2">Apply to partner.</h2>
          <p className="text-sm text-[color:var(--dt6-muted)] mb-8">
            We take partners seriously. Only apply if you have live client work in the America-First space.
          </p>
          {submitted ? (
            <div className="dt6-empty" data-testid="partner-application-success">
              <CheckCircle2 size={24} strokeWidth={1.5} className="mx-auto mb-3 text-[color:var(--dt6-success)]" />
              APPLICATION RECEIVED. WE'LL RESPOND WITHIN 3 BUSINESS DAYS.
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="partner-form">
              <div>
                <label className="dt6-label">Organization</label>
                <input className="dt6-input" required value={form.org_name} onChange={(e)=>setForm({...form,org_name:e.target.value})} data-testid="partner-org" />
              </div>
              <div>
                <label className="dt6-label">Contact name</label>
                <input className="dt6-input" required value={form.contact_name} onChange={(e)=>setForm({...form,contact_name:e.target.value})} data-testid="partner-name" />
              </div>
              <div>
                <label className="dt6-label">Work email</label>
                <input className="dt6-input" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="partner-email" />
              </div>
              <div>
                <label className="dt6-label">Phone</label>
                <input className="dt6-input" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="partner-phone" />
              </div>
              <div>
                <label className="dt6-label">Website</label>
                <input className="dt6-input" value={form.website} onChange={(e)=>setForm({...form,website:e.target.value})} data-testid="partner-website" />
              </div>
              <div>
                <label className="dt6-label">Est. monthly client volume</label>
                <input className="dt6-input" placeholder="e.g., 3-5 clients / 250k records" value={form.est_monthly_volume} onChange={(e)=>setForm({...form,est_monthly_volume:e.target.value})} data-testid="partner-volume" />
              </div>
              <div className="md:col-span-2">
                <label className="dt6-label">Services you offer</label>
                <input className="dt6-input" placeholder="e.g., mail, digital, phones, comms" value={form.services_offered} onChange={(e)=>setForm({...form,services_offered:e.target.value})} data-testid="partner-services" />
              </div>
              <div className="md:col-span-2">
                <label className="dt6-label">Notes</label>
                <textarea className="dt6-textarea" rows={4} value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} data-testid="partner-notes" />
              </div>
              {error && <div className="md:col-span-2 text-sm text-[color:var(--dt6-danger)]" data-testid="partner-error">{error}</div>}
              <div className="md:col-span-2 flex justify-end">
                <button className="dt6-btn dt6-btn-primary" disabled={loading} data-testid="partner-submit">
                  {loading ? "SUBMITTING..." : "Submit application"}
                </button>
              </div>
            </form>
          )}
        </div>
      </Section>

      <PublicFooter />
    </>
  );
}
